import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild } from '@angular/router';
import { environment } from '../../../environment/environment';
import { AssetContextService } from '../services/asset-context.service';
import { AuthService } from '../services/auth.service';
import { PermissionService } from '../services/permission.service';
import { UnauthorizedNavigationService } from '../services/unauthorized-navigation.service';
import { UserImpersonationService } from '../services/user-impersonation.service';

@Injectable({ providedIn: 'root' })
export class PermissionGuard implements CanActivate, CanActivateChild {
  private permissionService = inject(PermissionService);
  private authService = inject(AuthService);
  private assetContextService = inject(AssetContextService);
  private impersonationService = inject(UserImpersonationService);
  private unauthorizedNav = inject(UnauthorizedNavigationService);

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {
    if (environment.name === 'dev' && !this.impersonationService.isImpersonating()) {
      return true;
    }

    await this.authService.waitForUsers();

    const realUserId = this.authService.currentUserId();
    const effectiveUserId = this.impersonationService.getEffectiveUserId(realUserId);

    if (!effectiveUserId) {
      console.warn('[PermissionGuard] No effectiveUserId - denying access');
      this.unauthorizedNav.navigateToForbidden(route);
      return false;
    }

    const assetId = this.resolveAssetId(route);
    if (!assetId) {
      console.warn('[PermissionGuard] No assetId found - denying access');
      this.unauthorizedNav.navigateToForbidden(route);
      return false;
    }

    const fullPath = route.pathFromRoot
      .map(r => r.url.map(segment => segment.path).join('/'))
      .filter(p => p)
      .join('/');

    const routePath = fullPath.replace(/^assets\/\d+\//, '').replace(/\/\d+/g, '/:id');
    const hasAccess = await this.permissionService.canAccessRoute(effectiveUserId, assetId, routePath);

    if (!hasAccess) {
      console.warn(`[PermissionGuard] Access denied to ${fullPath} for user ${effectiveUserId} on asset ${assetId}`);
      this.unauthorizedNav.navigateToForbidden(route, { route: fullPath, userId: effectiveUserId, assetId });
    }

    return hasAccess;
  }

  canActivateChild(route: ActivatedRouteSnapshot): Promise<boolean> {
    return this.canActivate(route);
  }

  private resolveAssetId(route: ActivatedRouteSnapshot): number | null {
    const assetsIndex = route.pathFromRoot.findIndex(r => r.routeConfig?.path === 'assets');
    const assetIdFromRoute: unknown = assetsIndex >= 0 ? route.pathFromRoot[assetsIndex + 1]?.params['id'] : null;
    const assetIdParam: unknown = assetIdFromRoute ?? this.assetContextService.getCurrentAssetId();
    if (typeof assetIdParam === 'string') {
      const parsed = parseInt(assetIdParam, 10);
      return isNaN(parsed) ? null : parsed;
    }
    return typeof assetIdParam === 'number' ? assetIdParam : null;
  }
}
